/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Grid } from "@mui/material";
import { IntegratedSystem } from "../api/integratedsystems";
import { useNavigate, useParams } from "react-router-dom";
import { useSystemOrProject } from "../components/integrated-systems/hooks";
import {
  useUserActivitySummary,
  useUserActivityByField,
  useUserActivityMonthly,
  useSummaryByFieldOverBatches,
  useContactProblemSummary,
} from "../hooks/components/dashboard/useUserActivityData";

import { LoadingDisplay } from "../components/dashboard/LoadingDisplay";
import { Spacer } from "../components/utils/Spacer";
import { UserActivitySummary } from "../components/dashboard/UserActivitySummary";
import { UserActivityByField } from "../components/dashboard/UserActivityByField";
import { UserPerformanceCards } from "../components/dashboard/UserPerformanceCards";
import { UserLeaderboard } from "../components/dashboard/UserLeaderboard";
import { IndividualUserTimeline } from "../components/dashboard/IndividualUserTimeline";
import { UserSelector } from "../components/dashboard/UserSelector";
import { SummaryByFieldChart } from "../components/dashboard/SummaryByFieldChart";
import {
  CumulativeContactProblemChart,
  DailyContactProblemChart,
} from "../components/dashboard/ContactProblemCharts";

interface UserActivityDashboardProps {
  userSystems: IntegratedSystem[];
  setSelectedSystemId: (id: number) => void;
  dataProjectId?: number;
}

export const UserActivityDashboard = React.memo(
  (props: UserActivityDashboardProps) => {
    const navigate = useNavigate();
    const { integratedSystemId } = useParams();

    const systemOrProject = useSystemOrProject(
      props.userSystems,
      integratedSystemId
    );

    const userActivitySummary = useUserActivitySummary(
      systemOrProject,
      props.dataProjectId
    );

    const userActivityByField = useUserActivityByField(
      systemOrProject,
      props.dataProjectId
    );

    const userActivityMonthly = useUserActivityMonthly(
      systemOrProject,
      props.dataProjectId
    );

    const summaryByFieldOverBatches = useSummaryByFieldOverBatches(
      systemOrProject,
      props.dataProjectId
    );

    const contactProblemSummary = useContactProblemSummary(
      systemOrProject,
      props.dataProjectId
    );

    // Shared user selection state
    const [selectedUserId, setSelectedUserId] = useState<number | "all">("all");

    // Generate unique users from both datasets
    const uniqueUsers = useMemo(() => {
      const userMap = new Map();

      // Add users from monthly activity data
      userActivityMonthly.userActivityMonthly.forEach((activity) => {
        if (!userMap.has(activity.user_id)) {
          userMap.set(activity.user_id, {
            id: activity.user_id,
            name: activity.user_name,
          });
        }
      });

      // Add users from field activity data
      userActivityByField.userActivityByField.forEach((activity) => {
        if (!userMap.has(activity.user_id)) {
          userMap.set(activity.user_id, {
            id: activity.user_id,
            name: activity.user_name,
          });
        }
      });

      return Array.from(userMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    }, [
      userActivityMonthly.userActivityMonthly,
      userActivityByField.userActivityByField,
    ]);

    useEffect(() => {
      if (
        !integratedSystemId &&
        props.userSystems &&
        props.userSystems.length > 0
      ) {
        navigate(`/user-activity/${props.userSystems[0].id}`);
      }
    }, [integratedSystemId, props.userSystems, navigate]);

    if (
      userActivitySummary.isLoading &&
      userActivityByField.isLoading &&
      userActivityMonthly.isLoading &&
      summaryByFieldOverBatches.isLoading
    ) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom - User Activity" />
          <LoadingDisplay />
        </React.Fragment>
      );
    }

    if (!systemOrProject) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom - User Activity" />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Helmet title="Cirrom - User Activity" />

        <UserPerformanceCards
          userActivitySummary={userActivitySummary.userActivitySummary}
          isLoading={userActivitySummary.isLoading}
        />
        <Spacer />
        <UserLeaderboard
          userActivitySummary={userActivitySummary.userActivitySummary}
          isLoading={userActivitySummary.isLoading}
        />
        <Spacer />
        <UserActivitySummary
          userActivitySummary={userActivitySummary.userActivitySummary}
          isLoading={userActivitySummary.isLoading}
        />
        <Spacer />
        <SummaryByFieldChart
          summaryByFieldOverBatches={
            summaryByFieldOverBatches.summaryByFieldOverBatches
          }
          isLoading={summaryByFieldOverBatches.isLoading}
        />
        <Spacer />
        <CumulativeContactProblemChart
          contactProblemSummary={contactProblemSummary.contactProblemSummary}
          isLoading={contactProblemSummary.isLoading}
        />
        <Spacer />
        <DailyContactProblemChart
          contactProblemSummary={contactProblemSummary.contactProblemSummary}
          isLoading={contactProblemSummary.isLoading}
        />
        <Spacer />

        {/* Shared User Selector */}
        {!userActivityMonthly.isLoading &&
          !userActivityByField.isLoading &&
          uniqueUsers.length > 0 && (
            <UserSelector
              selectedUserId={selectedUserId}
              onUserChange={setSelectedUserId}
              users={uniqueUsers}
              label="Filter Charts by User"
            />
          )}

        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 8 }}>
            <IndividualUserTimeline
              userActivityMonthly={userActivityMonthly.userActivityMonthly}
              isLoading={userActivityMonthly.isLoading}
              selectedUserId={selectedUserId}
              uniqueUsers={uniqueUsers}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <UserActivityByField
              userActivityByField={userActivityByField.userActivityByField}
              isLoading={userActivityByField.isLoading}
              selectedUserId={selectedUserId}
              uniqueUsers={uniqueUsers}
            />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.userSystems === nextProps.userSystems &&
      prevProps.dataProjectId === nextProps.dataProjectId &&
      prevProps.setSelectedSystemId === nextProps.setSelectedSystemId
    );
  }
);
